import React, { useState, useEffect } from 'react';
import { Button, Card, Form, Input, Modal, Radio, Select, Space } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {useGetPrimaryGroups, useSearchByPrimaryGroupAndFullName} from "@/shared/api/client/nub-user-resource";
import {AccessControl} from "@/modules/action-flow/config";

const { Option } = Select;

interface AccessControlFormProps {
    value?: AccessControl[];
    onChange?: (value: AccessControl[]) => void;
}

const defaultAccessControl: AccessControl = {
    sourceType: 'CONFIG',
    type: 'GROUP',
    values: [],
    userGroup: undefined,
    approverCount: 1
};

export const AccessControlForm: React.FC<AccessControlFormProps> = ({
                                                                        value = [],
                                                                        onChange,
                                                                    }) => {
    const [controls, setControls] = useState<AccessControl[]>(value);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedGroup, setSelectedGroup] = useState<string>();

    // Update controls when value prop changes
    useEffect(() => {
        setControls(value);
    }, [value]);

    // Query primary groups
    const { data: groupsResponse } = useGetPrimaryGroups();

    // Query users by primary group
    const { data: usersResponse, isLoading: isLoadingUsers } = useSearchByPrimaryGroupAndFullName(
        selectedGroup || '',
        searchQuery,
        {
           // @ts-ignore
            enabled: !!selectedGroup && searchQuery.length > 0
        }
    );

    const handleModalSave = (formValues: AccessControl) => {
        const newControls = [...controls];
        if (editingIndex !== null) {
            newControls[editingIndex] = formValues;
        } else {
            newControls.push(formValues);
        }
        setControls(newControls);
        onChange?.(newControls);
        setIsModalVisible(false);
        setEditingIndex(null);
    };

    const handleDelete = (index: number) => {
        const newControls = controls.filter((_, i) => i !== index);
        setControls(newControls);
        onChange?.(newControls);
    };

    const handleEdit = (index: number) => {
        setEditingIndex(index);
        setIsModalVisible(true);
    };

    const renderAccessControlSummary = (control: AccessControl) => (
        <Space direction="vertical" size="small">
            <div>
                <span className="mr-2 font-bold">Source:</span>
                {control.sourceType}
            </div>
            <div>
                <span className="mr-2 font-bold">Type:</span>
                {control.type}
            </div>
            {control.sourceType === 'CONFIG' && (
                <div>
                    {control.type === 'USER' && control.userGroup && (
                        <div>
                            <span className="mr-2 font-bold">Group:</span>
                            {control.userGroup}
                        </div>
                    )}
                    <div>
                        <span className="mr-2 font-bold">Values:</span>

                        {
                            // @ts-ignore
                            control.values.length > 0 ? (
                            <ul className="list-disc ml-4">
                                {
                                    // @ts-ignore
                                    control.values.map((v) => (
                                    <li key={v}>{v}</li>
                                ))}
                            </ul>
                        ) : (
                            <span className="text-red-500">No values set</span>
                        )}
                    </div>
                </div>
            )}
            {control.sourceType === 'MAKER' && control.approverCount && (
                <div>
                    <span className="mr-2 font-bold">Approver Count:</span>
                    {control.approverCount}
                </div>
            )}
        </Space>
    );

    return (
        <div className="space-y-4">
            {controls.map((control, index) => (
                <Card
                    key={index}
                    size="small"
                    title="Access Control"
                    extra={
                        <Space>
                            <Button
                                type="text"
                                icon={<PlusOutlined />}
                                onClick={() => handleEdit(index)}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(index)}
                            />
                        </Space>
                    }
                >
                    {renderAccessControlSummary(control)}
                </Card>
            ))}

            <Button
                type="dashed"
                onClick={() => {
                    setEditingIndex(null);
                    setIsModalVisible(true);
                }}
                block
            >
                Add Access Control
            </Button>

            <Modal
                title={editingIndex !== null ? "Edit Access Control" : "Add Access Control"}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingIndex(null);
                }}
                footer={null}
                width={800}
            >
                <AccessControlEditor
                    initialValues={editingIndex !== null ? controls[editingIndex] : defaultAccessControl}
                    onSave={handleModalSave}
                    onCancel={() => {
                        setIsModalVisible(false);
                        setEditingIndex(null);
                    }}
                    groups={groupsResponse?.data || []}
                    usersResponse={usersResponse}
                    isLoadingUsers={isLoadingUsers}
                    onGroupChange={setSelectedGroup}
                    onSearch={setSearchQuery}
                    selectedGroup={selectedGroup}
                />
            </Modal>
        </div>
    );
};

interface AccessControlEditorProps {
    initialValues: AccessControl;
    onSave: (values: AccessControl) => void;
    onCancel: () => void;
    groups: any[];
    usersResponse: any;
    isLoadingUsers: boolean;
    onGroupChange: (group: string) => void;
    onSearch: (query: string) => void;
    selectedGroup?: string;
}

const AccessControlEditor: React.FC<AccessControlEditorProps> = ({
                                                                     initialValues,
                                                                     onSave,
                                                                     onCancel,
                                                                     groups,
                                                                     usersResponse,
                                                                     isLoadingUsers,
                                                                     onGroupChange,
                                                                     onSearch,
                                                                     selectedGroup,
                                                                 }) => {
    const [form] = Form.useForm();

    React.useEffect(() => {
        form.setFieldsValue(initialValues);
    }, [form, initialValues]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            onSave(values);
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
        >
            <Form.Item
                name="sourceType"
                label="Source Type"
                rules={[{ required: true }]}
            >
                <Radio.Group onChange={(e) => {
                    // If sourceType is SELF, set type to USER automatically
                    if (e.target.value === 'SELF') {
                        form.setFieldsValue({ type: 'USER' });
                    }
                }}>
                    <Radio.Button value="CONFIG">Configuration</Radio.Button>
                    <Radio.Button value="MAKER">Maker Form</Radio.Button>
                    <Radio.Button value="SELF">Self</Radio.Button>
                </Radio.Group>
            </Form.Item>

            <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                    prevValues.sourceType !== currentValues.sourceType
                }
            >
                {({ getFieldValue }) => {
                    const sourceType = getFieldValue('sourceType');
                    
                    // Only show Access Type if sourceType is not SELF
                    if (sourceType !== 'SELF') {
                        return (
                            <Form.Item
                                name="type"
                                label="Access Type"
                                rules={[{ required: true }]}
                            >
                                <Select
                                    onChange={() => {
                                        form.setFieldsValue({
                                            values: [],
                                            userGroup: undefined
                                        });
                                    }}
                                >
                                    <Option value="GROUP">Groups</Option>
                                    <Option value="USER">Users</Option>
                                </Select>
                            </Form.Item>
                        );
                    }
                    
                    return null;
                }}
            </Form.Item>

            <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                    prevValues.sourceType !== currentValues.sourceType ||
                    prevValues.type !== currentValues.type
                }
            >
                {({ getFieldValue }) => {
                    const sourceType = getFieldValue('sourceType');
                    const type = getFieldValue('type');

                    if (sourceType === 'CONFIG') {
                        if (type === 'USER') {
                            return (
                                <>
                                    <Form.Item
                                        name="userGroup"
                                        label="Select Primary Group"
                                        rules={[{ required: true }]}
                                    >
                                        <Select
                                            onChange={onGroupChange}
                                            loading={isLoadingUsers}
                                        >
                                            {groups.map(group => (
                                                <Option key={group.code} value={group.code}>
                                                    {group.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item
                                        name="values"
                                        label="Select Users"
                                        rules={[{ required: true }]}
                                    >
                                        <Select
                                            mode="multiple"
                                            showSearch
                                            placeholder="Search users..."
                                            onSearch={onSearch}
                                            loading={isLoadingUsers}
                                            disabled={!selectedGroup}
                                        >
                                            {usersResponse?.data?.map((user: any) => (
                                                <Option key={user.userId} value={user.userId}>
                                                    {user.fullName} ({user.userId})
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </>
                            );
                        }

                        if (type === 'GROUP') {
                            return (
                                <Form.Item
                                    name="values"
                                    label="Select Groups"
                                    rules={[{ required: true }]}
                                >
                                    <Select mode="multiple">
                                        {groups.map(group => (
                                            <Option key={group.code} value={group.code}>
                                                {group.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            );
                        }
                    }

                    if (sourceType === 'MAKER') {
                        if (type === 'USER') {
                            return (
                                <>
                                    <Form.Item
                                        name="userGroup"
                                        label="Select Primary Group"
                                        rules={[{ required: true }]}
                                    >
                                        <Select
                                            onChange={onGroupChange}
                                            loading={isLoadingUsers}
                                        >
                                            {groups.map(group => (
                                                <Option key={group.code} value={group.code}>
                                                    {group.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>


                                    <Form.Item
                                        name="approverCount"
                                        label="Approver Count"
                                        rules={[{ required: true }]}
                                    >
                                        <Input type="number" min={1} />
                                    </Form.Item>
                                </>
                            );
                        }
                        return (
                            <Form.Item
                                name="approverCount"
                                label="Approver Count"
                                rules={[{ required: true }]}
                            >
                                <Input type="number" min={1} />
                            </Form.Item>
                        );
                    }

                    if (sourceType === 'SELF') {
                        return (
                            <>

                                <p>Maker Will be responsible for approval</p>

                                <Form.Item
                                    name="type"
                                    label="Access Type"
                                    rules={[{required: true}]}
                                >
                                    <Select>
                                        <Option value="USER">Users</Option>
                                    </Select>
                                </Form.Item>
                            </>
                        );
                    }

                    return null;
                }}
            </Form.Item>

            <Form.Item className="mt-4">
                <Space>
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit">
                        Save
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
};